import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import buyingPostApi from '../../api/buyingPost';
import moment from 'moment';

// 格式化时间数组为字符串
const formatTimeArray = (timeArray) => {
  if (Array.isArray(timeArray) && timeArray.length >= 6) {
    return moment(new Date(timeArray[0], timeArray[1] - 1, timeArray[2], 
                           timeArray[3], timeArray[4], timeArray[5])).format('YYYY-MM-DD HH:mm:ss');
  }
  return '-';
};

// 分页查询求购帖
export const fetchBuyingPostList = createAsyncThunk(
  'buyingPost/fetchList',
  async (params = {}, { rejectWithValue }) => {
    try {
      const {
        page = 1, 
        pageSize = 10, 
        sortField = 'create_time', 
        sortOrder = 'desc',
        title,
        username,
        status,
        startTime,
        endTime,
        ...otherParams
      } = params;

      const queryParams = {
        page,
        pageSize,
        sortField,
        sortOrder
      };

      // 添加搜索参数
      if (title) queryParams.title = title;
      if (username) queryParams.username = username;
      if (status) queryParams.status = status;
      if (startTime) queryParams.startTime = startTime;
      if (endTime) queryParams.endTime = endTime;

      const response = await buyingPostApi.pageBuyingPosts(queryParams);
      
      // 处理响应数据
      const processedData = {
        records: (response.data?.records || []).map(item => ({
          ...item,
          createTime: formatTimeArray(item.createTime),
          updateTime: formatTimeArray(item.updateTime),
          
          // 状态映射
          statusText: {
            '审核中': '审核中',
            '审核通过': '审核通过',
            '审核驳回': '审核驳回',
            '已下架': '已下架'
          }[item.status] || item.status,

          // 价格处理
          priceRange: item.expectedMinPrice && item.expectedMaxPrice 
            ? `¥${item.expectedMinPrice} - ¥${item.expectedMaxPrice}` 
            : '未指定价格范围'
        })),
        total: response.data?.total || 0,
        current: response.data?.current || page,
        size: response.data?.size || pageSize,
        pages: response.data?.pages || 1
      };

      return processedData;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '获取求购帖列表失败' });
    }
  }
);

// 获取求购帖详情
export const fetchBuyingPostDetail = createAsyncThunk(
  'buyingPost/fetchDetail',
  async (id, { rejectWithValue }) => {
    try {
      const response = await buyingPostApi.getBuyingPostDetail(id);
      
      // 处理详情数据
      const detail = response.data;
      
      return {
        ...detail,
        createTime: formatTimeArray(detail.createTime),
        updateTime: formatTimeArray(detail.updateTime),
        
        // 状态映射
        statusText: {
          'OFF_SHELF': '已下架',
          'AUDITING': '审核中',
          'APPROVED': '审核通过',
          'REJECTED': '审核驳回'
        }[detail.status] || detail.status
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '获取求购帖详情失败' });
    }
  }
);

// 审核求购帖
export const auditBuyingPost = createAsyncThunk(
  'buyingPost/audit',
  async ({ id, status }, { rejectWithValue, dispatch }) => {
    try {
      const response = await buyingPostApi.auditBuyingPost(id, { status });
      
      // 刷新列表
      dispatch(fetchBuyingPostList());
      
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '审核求购帖失败' });
    }
  }
);

// 创建求购帖
export const createBuyingPost = createAsyncThunk(
  'buyingPost/create',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await buyingPostApi.createBuyingPost(data);
      
      // 刷新列表
      dispatch(fetchBuyingPostList());
      
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '创建求购帖失败' });
    }
  }
);

// 删除求购帖
export const deleteBuyingPost = createAsyncThunk(
  'buyingPost/delete',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const response = await buyingPostApi.deleteBuyingPost(id);
      
      // 刷新列表
      dispatch(fetchBuyingPostList());
      
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '删除求购帖失败' });
    }
  }
);

const buyingPostSlice = createSlice({
  name: 'buyingPost',
  initialState: {
    buyingPosts: [],
    total: 0,
    current: 1,
    pageSize: 10,
    loading: false,
    error: null,
    currentBuyingPost: null,
    buyingPostDetail: null,
    detailLoading: false,
    detailError: null
  },
  reducers: {
    setCurrentBuyingPost: (state, action) => {
      state.currentBuyingPost = action.payload;
    }
  },
  extraReducers: (builder) => {
    // 获取求购帖列表
    builder.addCase(fetchBuyingPostList.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchBuyingPostList.fulfilled, (state, action) => {
      state.loading = false;
      state.buyingPosts = action.payload.records;
      state.total = action.payload.total;
      state.current = action.payload.current;
      state.pageSize = action.payload.size;
    });
    builder.addCase(fetchBuyingPostList.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 获取求购帖详情
    builder.addCase(fetchBuyingPostDetail.pending, (state) => {
      state.detailLoading = true;
      state.detailError = null;
    });
    builder.addCase(fetchBuyingPostDetail.fulfilled, (state, action) => {
      state.detailLoading = false;
      state.buyingPostDetail = action.payload;
    });
    builder.addCase(fetchBuyingPostDetail.rejected, (state, action) => {
      state.detailLoading = false;
      state.detailError = action.payload;
    });

    // 创建求购帖
    builder.addCase(createBuyingPost.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createBuyingPost.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(createBuyingPost.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 审核求购帖
    builder.addCase(auditBuyingPost.fulfilled, (state) => {
      // 可以在这里添加额外的状态更新逻辑
    });

    // 删除求购帖
    builder.addCase(deleteBuyingPost.fulfilled, (state) => {
      // 可以在这里添加额外的状态更新逻辑
    });
  }
});

export const { setCurrentBuyingPost } = buyingPostSlice.actions;
export default buyingPostSlice.reducer;
